import { AbstractControl, FormGroup } from "@angular/forms";
import { DestroyableMixin } from "apps-module/app/shared-library/mixins/destroyable.mixin";
import { EmptyInstance } from "core-library/mixins/empty-instance";
import _cloneDeep from "lodash/cloneDeep";
import { distinctUntilChanged, Observable, Subject, takeUntil } from "rxjs";

export abstract class BaseObservableForm<TFormModel> extends DestroyableMixin(EmptyInstance) {
    private _onFormSubmitted = new Subject<TFormModel>();
    private _onFormChanged = new Subject<TFormModel>();

    public get onFormChanged(): Observable<TFormModel> {
        return this._onFormChanged.pipe(
            distinctUntilChanged()
        );
    }

    public get onFormSubmitted(): Observable<TFormModel> {
        return this._onFormSubmitted.asObservable();
    }

    protected constructor(
        protected formInstance: FormGroup,
        protected initialFormState: TFormModel
    ) {
        super();

        this.onFormSubmitted
            .pipe(takeUntil(this.onDestroy))
            .subscribe((model) => {
                this.updateFormState(model);
            });

        setTimeout(() => this.initChangeListener(), 0);
    }

    protected abstract bindOnChangeSubscription(): Observable<TFormModel>;

    public abstract reset(): void;

    private initChangeListener(): void {
        const changes$ = this.bindOnChangeSubscription();

        if (changes$) {
            changes$
                .pipe(takeUntil(this.onDestroy))
                .subscribe((model) => {
                    this.updateFormState(model);
                    this._onFormChanged.next(model);
                });
        }
    }

    private updateFormState(model: TFormModel): void {
        this.formInstance.patchValue(model, { emitEvent: false });
        this.initialFormState = model;
    }

    public getFormControl(alias: string): AbstractControl {
        return this.formInstance.get(alias);
    }

    public submit(partialFormModel: object = {}): void {
        if (this.isFormValid()) {
            this._onFormSubmitted.next(this.getFormModel(partialFormModel));
        }
    }

    public getValue(): TFormModel {
        return this.getFormModel();
    }

    protected getFormModel(partialFormModel: object = {}): TFormModel {
        const model = _cloneDeep(this.formInstance.value) as TFormModel;
        return _cloneDeep({ ...model, ...partialFormModel });
    }

    protected isFormValid(): boolean {
        return this.formInstance.valid;
    }
}
